import { useState } from 'react';
import { AlertCircle, ExternalLink, Users, HardDrive, Gamepad, Copy, Check, Clock3, Cpu, MemoryStick } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type {
  AppliedServerSpec,
  OwnerServerOperation,
  ServerAutoShutdownState,
  ServerMonitoringSnapshot,
} from '../../types/server';
import { formatBytes, formatNanoCpus } from '../../utils/resourceFormatting';
import { isMonitoringStale } from '../../utils/monitoring';

interface Server {
  _id: string;
  name: string;
  displayName: string;
  status: 'creating' | 'starting' | 'running' | 'stopping' | 'stopped' | 'error' | 'suspended';
  subdomain: string;
  connectionAddress: string | null;
  directConnectionAddress?: string | null;
  packageId?: {
    name: string;
    displayName: string;
    ram: number;
    cpu?: number;
    storage?: number;
    maxPlayers: number;
    price: number;
  } | null;
  appliedSpec?: AppliedServerSpec;
  gameSettings: {
    version: string;
    difficulty: string;
    mode: string;
  };
  monitoring: ServerMonitoringSnapshot;
  autoShutdown?: ServerAutoShutdownState;
  ownerOperation?: OwnerServerOperation | null;
  dnsRecords?: Array<{ type: string; name: string; content: string }>;
  startupProgress?: {
    phase: string;
    percent: number;
    details?: string;
    startedAt: string;
    completedAt?: string;
  };
  createdAt: string;
}

interface ServerCardProps {
  server: Server;
}

export function ServerCard({ server }: ServerCardProps) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const { t, i18n } = useTranslation();
  const appliedSpec = server.appliedSpec;
  const packageName = appliedSpec?.package.displayName
    || server.packageId?.displayName
    || t('serverDetails.legacyPackage');
  const maxPlayers = appliedSpec?.limits.maxPlayers ?? server.monitoring.playerStats.maxPlayers;
  const monitoringStale = isMonitoringStale(server.monitoring);
  const scheduledTimestamp = server.autoShutdown?.scheduledFor
    ? new Date(server.autoShutdown.scheduledFor).getTime()
    : Number.NaN;
  const autoShutdownScheduledFor = server.status === 'running'
    && !monitoringStale
    && server.monitoring.playerStats.success
    && Number.isFinite(scheduledTimestamp)
      ? new Date(scheduledTimestamp)
      : null;
  const memory = appliedSpec
    ? formatBytes(appliedSpec.resources.memoryBytes)
    : (server.packageId?.ram !== undefined ? `${server.packageId.ram} GB` : '—');
  const cpu = appliedSpec
    ? formatNanoCpus(appliedSpec.resources.nanoCpus)
    : (server.packageId?.cpu !== undefined ? `${server.packageId.cpu} vCPU` : '—');
  const storage = appliedSpec
    ? formatBytes(appliedSpec.resources.storageBytes)
    : (server.packageId?.storage !== undefined ? `${server.packageId.storage} GB` : '—');

  const copyAddress = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'running':
        return 'badge-online';
      case 'stopped':
        return 'badge-offline';
      case 'creating':
      case 'starting':
      case 'stopping':
        return 'badge-warning';
      case 'error':
        return 'badge-error';
      case 'suspended':
        return 'badge-error';
      default:
        return 'badge-offline';
    }
  };

  const ownerPhaseLabel = server.ownerOperation
    ? t(`serverOperation.phases.${server.ownerOperation.phase}`)
    : null;
  const ownerErrorLabel = server.ownerOperation?.error
    ? t(`serverOperation.errors.${server.ownerOperation.error.code}`)
    : null;
  const isProcessing = ['creating', 'starting', 'stopping'].includes(server.status)
    || server.ownerOperation?.status === 'in_progress';

  return (
    <div className="card-glow p-5">
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-neon-500/10 rounded-lg flex items-center justify-center">
            <Gamepad className="w-5 h-5 text-neon-400" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-100">{server.displayName}</h3>
            <p className="text-xs text-gray-500">{packageName}</p>
            <p className="text-[11px] text-gray-600 font-mono">{server.name}</p>
          </div>
        </div>
        <span className={getStatusBadge(server.status)}>
          {server.status === 'running' && <span className="status-dot-online" />}
          {t(`serverStatus.${server.status}`)}
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="text-center p-2 bg-dark-700/50 rounded-lg">
          <Users className="w-4 h-4 mx-auto text-gray-500 mb-1" />
          <div className="text-sm font-medium text-gray-200">
            {monitoringStale ? '—' : server.monitoring.playerStats.currentPlayers}/{maxPlayers}
          </div>
          <div className="text-xs text-gray-500">{t('serverCard.players')}</div>
        </div>
        <div className="text-center p-2 bg-dark-700/50 rounded-lg">
          <MemoryStick className="w-4 h-4 mx-auto text-gray-500 mb-1" />
          <div className="text-sm font-medium text-gray-200">{memory}</div>
          <div className="text-xs text-gray-500">{t('serverCard.ram')}</div>
        </div>
        <div className="text-center p-2 bg-dark-700/50 rounded-lg">
          <Cpu className="w-4 h-4 mx-auto text-gray-500 mb-1" />
          <div className="text-sm font-medium text-gray-200">{cpu}</div>
          <div className="text-xs text-gray-500">{t('serverCard.cpu')}</div>
        </div>
        <div className="text-center p-2 bg-dark-700/50 rounded-lg">
          <HardDrive className="w-4 h-4 mx-auto text-gray-500 mb-1" />
          <div className="text-sm font-medium text-gray-200">{storage}</div>
          <div className="text-xs text-gray-500">{t('serverCard.storage')}</div>
        </div>
      </div>

      {server.autoShutdown?.enabled && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-500/20 bg-amber-500/10 p-3 text-xs text-amber-200">
          <Clock3 className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {autoShutdownScheduledFor
              ? t('serverCard.autoShutdownScheduled', { time: autoShutdownScheduledFor.toLocaleString(i18n.language) })
              : server.status === 'running'
                ? t('serverCard.autoShutdownWaiting', { minutes: server.autoShutdown.idleMinutes })
                : t('serverCard.autoShutdownEnabled', { minutes: server.autoShutdown.idleMinutes })}
          </span>
        </div>
      )}

      {server.ownerOperation?.status === 'failed' && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-xs text-red-200">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{ownerErrorLabel || server.ownerOperation.error?.message}</span>
        </div>
      )}

      {/* Address */}
      {server.status === 'running' && server.connectionAddress && (
        <div className="mb-4 p-3 bg-dark-900/50 rounded-lg border border-dark-600">
          <div className="text-xs text-gray-500 mb-1">{t('serverCard.serverAddress')}</div>
          <div className="flex items-center justify-between gap-2">
            <code className="text-sm text-neon-300 font-mono truncate">
              {server.connectionAddress}
            </code>
            <button
              onClick={(e) => {
                e.stopPropagation();
                copyAddress(server.connectionAddress || '');
              }}
              className="btn-ghost p-1 shrink-0"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-neon-400" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          </div>
          {server.directConnectionAddress && server.directConnectionAddress !== server.connectionAddress && (
            <div className="text-xs text-gray-600 mt-1 font-mono">{server.directConnectionAddress}</div>
          )}
        </div>
      )}

      {/* Progress */}
      {isProcessing && (
        <div className="mb-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>{ownerPhaseLabel || (server.status === 'starting' && server.startupProgress?.phase ? server.startupProgress.phase : `${t(`serverStatus.${server.status}`)}...`)}</span>
            {!ownerPhaseLabel && server.status === 'starting' && server.startupProgress && (
              <span>{server.startupProgress.percent}%</span>
            )}
          </div>
          <div className="progress-bar">
            <div className="progress-fill animate-pulse" style={{ width: `${!ownerPhaseLabel && server.status === 'starting' && server.startupProgress ? server.startupProgress.percent : 60}%`, transition: 'width 0.5s ease' }} />
          </div>
        </div>
      )}

      {/* Details Button */}
      <div className="flex">
        <button
          onClick={() => navigate(`/servers/${server._id}`)}
          className="btn-primary w-full text-sm py-2"
        >
          <ExternalLink className="w-4 h-4" />
          {t('serverCard.viewDetails')}
        </button>
      </div>
    </div>
  );
}
